import { safeFormatMessage } from '@10xlabs/utils'
import { useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Checkbox } from 'components/v2/Checkbox'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { FilePreview, InputFile, MB } from 'components/v2/InputFile'
import { Label } from 'components/v2/Label'
import { Radio, RadioWrapper } from 'components/v2/Radio'
import { Row } from 'components/v2/Row'
import { INewPartnerStep1 } from 'features/Partners/types'
import { GTBusinessPartnerTaxPersonType } from 'gql/types.generated'
import { HelpOutlineIcon } from 'components/Icons/HelpOutlineIcon'
import Tooltip from 'components/v2/Tooltip'
import { isRepseIdRequired, TAX_PERSON_TYPE_LIST, taxPersonTypeMessages } from 'features/Partners/utils'

const messages = defineMessages({
  companyNamePlaceholder: {
    id: 'partners.form.companyName.placeholder',
    defaultMessage: 'Enter the company name'
  },
  businessNamePlaceholder: {
    id: 'partners.form.businessName.placeholder',
    defaultMessage: 'Enter the business name'
  },
  taxIdPlaceholder: {
    id: 'partners.form.taxId.placeholder',
    defaultMessage: 'Enter RFC'
  },
  curpPlaceholder: {
    id: 'partners.form.curp.placeholder',
    defaultMessage: 'Enter CURP'
  },
  taxRegimePlaceholder: {
    id: 'partners.form.taxRegime.placeholder',
    defaultMessage: 'Enter Tax Regime'
  },
  proofOfTaxSituationPlaceholder: {
    id: 'partners.form.proofOfTaxSituation.placeholder',
    defaultMessage: 'Enter link'
  },
  repseIdPlaceholder: {
    id: 'partners.form.repseId.placeholder',
    defaultMessage: 'Enter REPSE ID'
  },
  repseLinkPlaceholder: {
    id: 'partners.form.repseLink.placeholder',
    defaultMessage: 'Enter link'
  }
})

const CompanyNameLabelWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

const StyledHelpOutlineIcon = styled(HelpOutlineIcon)`
  fill: #916ee8;
  width: 20px;
  height: 20px;
`

const CheckboxContainer = styled.div`
  margin-top: 8px;
`

export const TaxComplianceFormCard = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, handleBlur, touched, errors } = useFormikContext<INewPartnerStep1>()

  const handleTaxPersonTypeChange = ({ target: { id: value } }: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('taxPersonType', value)
  }

  const handleUsePartnerNameChange = async ({
    target: { checked }
  }: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    await setFieldValue('usePartnerName', checked)

    if (checked) {
      setFieldValue('businessName', values.name)
    }
  }

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="partners.form.section.taxCompliance.title" defaultMessage="Tax Compliance" />
      </Heading.H4>
      <Row noMargin>
        <Column width="50%">
          <Label>
            <FormattedMessage id="partners.form.taxPersonType.label" defaultMessage="Tax person type" />
          </Label>
          <RadioWrapper>
            {TAX_PERSON_TYPE_LIST.map((taxPersonType: GTBusinessPartnerTaxPersonType): ReactElement => {
              return (
                <Radio
                  id={taxPersonType}
                  key={taxPersonType}
                  name="taxPersonType"
                  checked={values.taxPersonType === taxPersonType}
                  onChange={handleTaxPersonTypeChange}
                >
                  {safeFormatMessage({
                    intl,
                    key: taxPersonType,
                    messages: taxPersonTypeMessages
                  })}
                </Radio>
              )
            })}
          </RadioWrapper>
          {touched.taxPersonType && errors.taxPersonType && <ErrorMessage>{errors.taxPersonType}</ErrorMessage>}
        </Column>
      </Row>
      {values.taxPersonType === GTBusinessPartnerTaxPersonType.LegalEntity && (
        <Row noMargin>
          <Column width="50%">
            <Input
              id="companyName"
              value={values.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.companyNamePlaceholder)}
              label={
                <CompanyNameLabelWrapper>
                  <FormattedMessage id="partners.form.companyName.label" defaultMessage="Company Name" />
                  <Tooltip
                    content={
                      <FormattedMessage
                        id="partners.form.companyName.label.tooltip.description"
                        defaultMessage="Razón social"
                      />
                    }
                    position="right"
                  >
                    <StyledHelpOutlineIcon />
                  </Tooltip>
                </CompanyNameLabelWrapper>
              }
              touched={touched.companyName}
              errors={errors.companyName}
            />
          </Column>
          <Column width="50%">
            <Input
              id="businessName"
              value={values.businessName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.businessNamePlaceholder)}
              label={<FormattedMessage id="partners.form.businessName.label" defaultMessage="Business Name" />}
              touched={touched.businessName}
              errors={errors.businessName}
            />
            <CheckboxContainer>
              <Checkbox id="usePartnerName" checked={values.usePartnerName} onChange={handleUsePartnerNameChange}>
                <FormattedMessage id="partners.form.usePartnerName.label" defaultMessage="Use partner's name" />
              </Checkbox>
            </CheckboxContainer>
          </Column>
        </Row>
      )}
      <Row noMargin>
        <Column width="50%">
          <Row>
            <Column>
              <Input
                id="taxId"
                value={values.taxId}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.taxIdPlaceholder)}
                label={<FormattedMessage id="partners.form.taxId.label" defaultMessage="RFC" />}
                touched={touched.taxId}
                errors={errors.taxId}
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <InputFile
                placeholder={
                  <FormattedMessage id="partners.form.taxIdFile.placeholder" defaultMessage="RFC Constancy" />
                }
                onChange={(file): void => {
                  if (file instanceof File) {
                    setFieldValue('taxIdDocument', {
                      file,
                      name: file.name,
                      path: '',
                      id: ''
                    })
                  }
                }}
                touched={touched.taxIdDocument as unknown as boolean}
                errors={errors.taxIdDocument as unknown as string}
                accept={{
                  'application/pdf': [],
                  'image/jpeg': [],
                  'image/jpg': [],
                  'image/png': []
                }}
                maxSize={16 * MB}
              />
            </Column>
          </Row>
          {(values.taxIdDocument?.file || values.taxIdDocument?.path) && (
            <Row>
              <Column>
                <FilePreview
                  file={values.taxIdDocument.file || (values.taxIdDocument.path as string)}
                  filename={values.taxIdDocument.name}
                  onRemoveFile={(): void => {
                    setFieldValue('taxIdDocument', {
                      file: '',
                      name: '',
                      path: '',
                      id: ''
                    })
                  }}
                />
              </Column>
            </Row>
          )}
        </Column>
        <Column width="50%">
          <Row>
            <Column>
              <Input
                id="curp"
                value={values.curp}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.curpPlaceholder)}
                label={<FormattedMessage id="partners.form.curp.label" defaultMessage="Curp" />}
                touched={touched.curp}
                errors={errors.curp}
                optional
              />
            </Column>
          </Row>
          <Row>
            <Column>
              <InputFile
                placeholder={<FormattedMessage id="partners.form.curpFile.placeholder" defaultMessage="CURP" />}
                onChange={(file): void => {
                  if (file instanceof File) {
                    setFieldValue('curpDocument', {
                      file,
                      name: file.name,
                      path: '',
                      id: ''
                    })
                  }
                }}
                accept={{
                  'application/pdf': [],
                  'image/jpeg': [],
                  'image/jpg': [],
                  'image/png': []
                }}
                maxSize={16 * MB}
              />
            </Column>
          </Row>
          {(values.curpDocument?.file || values.curpDocument.path) && (
            <Row>
              <Column>
                <FilePreview
                  file={values.curpDocument?.file || (values.curpDocument.path as string)}
                  filename={values.curpDocument?.name}
                  onRemoveFile={(): void => {
                    setFieldValue('curpDocument', {
                      file: '',
                      name: '',
                      path: '',
                      id: ''
                    })
                  }}
                />
              </Column>
            </Row>
          )}
        </Column>
      </Row>
      <Row>
        <Column>
          <Input
            id="taxRegime"
            value={values.taxRegime}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.taxRegimePlaceholder)}
            label={<FormattedMessage id="partners.form.taxRegime.label" defaultMessage="Tax Regime" />}
            touched={touched.taxRegime}
            errors={errors.taxRegime}
            optional
          />
        </Column>
        <Column>
          <Input
            id="proofOfTaxSituation"
            value={values.proofOfTaxSituation}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.proofOfTaxSituationPlaceholder)}
            label={
              <FormattedMessage id="partners.form.proofOfTaxSituation.label" defaultMessage="Proof of tax situation" />
            }
            touched={touched.proofOfTaxSituation}
            errors={errors.proofOfTaxSituation}
            optional
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <Input
            id="repseId"
            value={values.repseId}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.repseIdPlaceholder)}
            label={<FormattedMessage id="partners.form.repseId.label" defaultMessage="Repse Id" />}
            touched={touched.repseId}
            errors={errors.repseId}
            optional={!isRepseIdRequired(values.taxPersonType)}
          />
        </Column>
        <Column>
          <Input
            id="repseLink"
            value={values.repseLink}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.repseLinkPlaceholder)}
            label={<FormattedMessage id="partners.form.repseLink.label" defaultMessage="Repse link" />}
            touched={touched.repseLink}
            errors={errors.repseLink}
            optional={!isRepseIdRequired(values.taxPersonType)}
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
